/*
 * File: /src/shared/axiosWrapper.js
 * Created Date: Saturday April 6th 2019
 * Author: Nicolas Wegener, Roy Collins
 * -----
 * Last Modified: Saturday, 6th April 2019 10:08:38 pm
 * Modified By: Nicolas Wegener (wegenern@gmail.com)
 * -----
 * Copyright (c) 2019 AdBase, Inc.
 */
import axios from 'axios';
import { getBasePath } from './axiosBasePath';
// Eager import prevents nested imports
import AuthService from 'src/services/authServices';
import router from 'src/router/index';
import store from 'src/store/index';

// Base
const base = {
  baseURL: getBasePath()
};

/*
const jwt = localStorage.getItem('s2-access-token') || undefined;
if (jwt !== undefined) {
  base.headers = {
    'Content-Type': 'application/json',
    Authorization: 'Bearer ' + jwt
  };
}
*/

export const authedAxios = axios.create(base);
// Public will not have interceptors
export const publicAxios = axios.create(base);

// Locking singleton for preventing race condition on refresh
let refreshProm = undefined;
let refreshResolve = undefined;
let refreshTimeout = undefined;


authedAxios.interceptors.request.use(
  config => {
    const token = localStorage.getItem('s2-access-token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

authedAxios.interceptors.response.use((response) => {
  // Nothing to do on happy path
  return response;
}, async function (err) {
  // Not a request error so let it bubble
  if (!err.response) {
    console.log(' not processing this error in interceptor', err);
    throw (err);
  }
  const orgConfig = err.response.config;
  // Refresh token on 401 automatically, one time
  if (err.response.status === 401 && !orgConfig._retry) {
    orgConfig._retry = true;

    await _lockForRefresh();
    // Store a reference to our singleton for odd timing cases
    // when the timeout expires and another thread is locking
    const thisProm = refreshProm;

    let accessToken = localStorage.getItem('s2-access-token');
    var raw = accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    var jwt = JSON.parse(decodeURIComponent(atob(raw).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join('')));

    // Make sure the token is expired, Another thread might have updated it for us
    if (Date.now() >= jwt.exp * 1000) {
      // Token is expired get a new one
      accessToken = await _refreshAccessToken(thisProm);
      // Only the first to get a new token will unlock and allow the rest of the thread to go through
      if (!accessToken) {
        // refresh token is expired too. Login again
        // Clear the timeout and promise
        await _unlockRefresh(thisProm);
        store.dispatch('auth/logout');
        router.push('/login');

        return Promise.reject();
      }
      await _unlockRefresh(thisProm);
    }
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + accessToken;
    orgConfig.headers['Authorization'] = `Bearer ${accessToken}`;
    return axios(orgConfig);
  }
  // Let the original error bubble
  throw(err);
});

const _lockForRefresh = async () => {
  // This promise will release automatically after timeout
  if (refreshTimeout == undefined) {
    refreshProm = new Promise((resolve, reject) => {
      refreshResolve = resolve;
      refreshTimeout = setTimeout(async () => {
        // Force the lock to release after 5 minutes
        refreshTimeout = undefined;
        if (refreshProm) {
          await _unlockRefresh(refreshProm);
        }
      },
      30000 // 5 min
      );
    });
    // First thread get to pass
    return;
  }
  // All other threads wait on lock
  await refreshProm;
  return;
}

const _unlockRefresh = async (prom) => {
  if (prom == refreshProm) {
    clearTimeout(refreshTimeout);
    refreshTimeout = undefined;
    refreshProm = undefined;
    refreshResolve();
    refreshResolve = undefined;
  }
  else {
    console.log('Wrong promise, timing error?', prom, refreshProm);
  }
}

const _refreshAccessToken = async (prom) => {
  // Use a singleton lock to prevent race conditions on all the threads
  const refreshToken = localStorage.getItem('s2-refresh-token');
  if (!refreshToken) {
    return Promise.reject('No refresh token');
  }
  let newToken;
  try {
    newToken = await AuthService.refreshAccessToken(refreshToken);
  }
  catch (err) {
    if (!err.response) {
      throw err;
    }
    if (err.response.status === 401) {
      return;
      // throw err;
    }
    // All other errors, 422, 500, etc
    throw err;
  }
  // Any other error;
  if (!newToken) {
    return Promise.reject('Refresh Token is invalid, login again');
  }

  // Update the local storage versions
  localStorage.setItem(
      's2-access-token',
      newToken
  );
  // TODO: Eventually rotate refresh tokens on refresh too
  /*
  localStorage.setItem(
      'refreshToken',
      newRefreshToken
  );
  */
  return newToken;
};
